import React, { useState, useRef, useEffect } from 'react';

const CHAPTER_CONTEXT: Record<string, string[]> = {
  prologue: [
    "This chapter introduces why building an OS matters.",
    "The key insight: everything above the kernel is an abstraction the kernel provides.",
  ],
  joeos: [
    "Joe's OS writes directly to VGA memory at 0xB8000.",
    "Each VGA character cell is 2 bytes: character + color attribute.",
    "The while(1) at the end prevents the CPU from executing garbage.",
  ],
  osx86: [
    "OSx86 implements its own bootloader in assembly.",
    "The GDT (Global Descriptor Table) is required for protected mode.",
    "A far jump flushes the pipeline after enabling protected mode.",
  ],
  "riscv-boot": [
    "OpenSBI runs in M-mode and jumps to our kernel at 0x80200000.",
    "The boot function sets up the stack pointer before any function calls.",
    "BSS must be manually zeroed in bare metal.",
  ],
  "riscv-memory": [
    "SV32 uses 2-level page tables with 10-bit VPNs.",
    "Each page is 4KB. The offset is 12 bits.",
    "sfence.vma flushes the TLB after changing page tables.",
  ],
  "riscv-processes": [
    "Context switching saves callee-saved registers on the kernel stack.",
    "sscratch holds the kernel stack top while in user mode.",
    "The scheduler finds the next RUNNABLE process and switches to it.",
  ],
  "riscv-shell": [
    "The shell runs in user mode and uses ecall for syscalls.",
    "sret drops from S-mode to U-mode.",
    "The shell binary is mapped at USER_BASE (0x1000000).",
  ],
  epilogue: [
    "The kernel is just a program that runs first.",
    "Next steps: filesystem, ELF loader, more syscalls.",
  ],
};

const RESPONSES: Record<string, string> = {
  memory: "Virtual memory lets each process think it has the whole address space. The MMU translates virtual→physical using page tables. In SV32, a 32-bit VA splits into VPN[1] (10 bits), VPN[0] (10 bits), and offset (12 bits). The satp register points to the root page table.",
  syscall: "Syscalls are the user→kernel boundary. The user puts args in registers (a0-a5), syscall number in a3, then executes `ecall`. The CPU traps to stvec, the kernel handles it, then `sret` returns to user mode with the result in a0.",
  process: "A process = saved CPU state + memory. Context switch saves callee-saved registers (s0-s11, ra, sp) to the old process's kernel stack, then restores from the new one. The key insight: `switch_context` doesn't return immediately — it returns when someone switches *back* to this process.",
  panic: "Every panic in this project was a real bug. scause=0xc = instruction page fault (code not mapped). scause=0xf = store page fault (stack not mapped or sscratch wrong). The fix was always in the setup — page tables, sscratch, stack mapping.",
  boot: "The boot chain: QEMU → OpenSBI (M-mode) → kernel (S-mode). OpenSBI sets up hardware and provides SBI calls (console, timer). The kernel's boot function sets sp and jumps to kernel_main. Without setting sp first, any function call would corrupt memory.",
  gdt: "The GDT defines memory segments for protected mode. Each entry has base, limit, and access bits. The code segment is executable+readable, data segment is read/write. After loading the GDT and setting CR0.PE=1, you must do a far jump to flush the pipeline.",
  default: "I can explain any concept from the chapters. Ask about virtual memory, syscalls, context switching, page tables, the boot process, or the bugs we encountered.",
};

function getResponse(msg: string, chapterId: string): string {
  const m = msg.toLowerCase();
  
  if (m.includes('memory') || m.includes('page') || m.includes('virtual'))
    return RESPONSES.memory;
  if (m.includes('syscall') || m.includes('ecall') || m.includes('trap'))
    return RESPONSES.syscall;
  if (m.includes('process') || m.includes('context') || m.includes('switch'))
    return RESPONSES.process;
  if (m.includes('panic') || m.includes('bug') || m.includes('fault'))
    return RESPONSES.panic;
  if (m.includes('boot') || m.includes('opensbi') || m.includes('stack'))
    return RESPONSES.boot;
  if (m.includes('gdt') || m.includes('segment') || m.includes('protected mode'))
    return RESPONSES.gdt;
  
  const context = CHAPTER_CONTEXT[chapterId] || [];
  if (context.length > 0) {
    return `Current chapter insights:\n\n• ${context.join('\n• ')}\n\nAsk me about specific concepts like "how does virtual memory work?" or "what caused the panics?"`;
  }
  
  return RESPONSES.default;
}

interface Message {
  type: 'user' | 'bot';
  text: string;
}

interface AIAgentProps {
  chapterId: string;
}

export default function AIAgent({ chapterId }: AIAgentProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { type: 'bot', text: 'Ask me anything about the OS concepts. Try "explain virtual memory" or "what is a syscall?"' },
  ]);
  const [input, setInput] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const send = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { type: 'user', text: userMsg }]);
    
    setTimeout(() => {
      const response = getResponse(userMsg, chapterId);
      setMessages((m) => [...m, { type: 'bot', text: response }]);
    }, 300);
  };

  return (
    <div className="ai-agent">
      {open && (
        <div className="ai-panel">
          <div className="ai-header">
            <div className="ai-status-dot" />
            <div className="ai-header-text">
              <div className="ai-header-title">OS Assistant</div>
              <div className="ai-header-sub">ask about any concept</div>
            </div>
          </div>
          <div className="ai-messages" ref={messagesRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`ai-message ${msg.type}`}>
                {msg.text}
              </div>
            ))}
          </div>
          <div className="ai-input-area">
            <input
              className="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="ask a question..."
            />
            <button className="ai-send" onClick={send}>→</button>
          </div>
        </div>
      )}
      <button className="ai-toggle" onClick={() => setOpen(!open)}>
        {open ? '✕' : '?'}
      </button>
    </div>
  );
}
